import { TravelBallSignupError } from '../../../services/travelBallSignup';
import type {
  TravelBallFamilyJoinRequest,
  TravelBallFamilyOutgoingLink,
  TravelBallFamilyPlayer,
  TravelBallGuardianRelationship,
} from '../../../types/travelBallFamily';

/**
 * Plain-word copy for linking players and parents/guardians (Account) and for the guardian's player
 * switcher. `mobile/utils/travel-ball-family-link-copy.ts` is a line-for-line port; change them together.
 */

/** What the API answers to every link request, account or not. Shown when it sends nothing back. */
export const LINK_REQUEST_SENT_MESSAGE = 'If an account exists, an invite has been sent.';

export const RELATIONSHIP_LABELS: Record<TravelBallGuardianRelationship, string> = {
  mother: 'Mother',
  father: 'Father',
  guardian: 'Guardian',
  other: 'Other',
};

/** "Mother", or nothing for a missing or unknown value. */
export const relationshipLabel = (value?: string | null): string =>
  value && value in RELATIONSHIP_LABELS
    ? RELATIONSHIP_LABELS[value as TravelBallGuardianRelationship]
    : '';

export const fullName = (p: { firstName: string; lastName: string }): string =>
  `${p.firstName} ${p.lastName}`.trim() || 'Unnamed';

/** "Riley Anderson (riley@example.com)", or just the name when there is no email. */
export const nameWithEmail = (p: { firstName: string; lastName: string; email?: string | null }): string =>
  p.email?.trim() ? `${fullName(p)} (${p.email.trim()})` : fullName(p);

/** "Riley A.": a linked player in the switcher. */
export const shortName = (p: { firstName: string; lastName: string }): string => {
  const first = p.firstName.trim();
  const initial = p.lastName.trim().charAt(0);
  if (!first) return initial ? `${initial}.` : 'Unnamed';
  return initial ? `${first} ${initial}.` : first;
};

/** The key a guardian's selection is stored under: the player's account, or the link while they wait. */
export const playerSelectionKey = (player: TravelBallFamilyPlayer): string =>
  player.playerUserId ? player.playerUserId : `link:${player.linkId}`;

/** A request a parent/guardian made for this player rather than one the player made. */
export const isRequestedByGuardian = (request: TravelBallFamilyJoinRequest): boolean =>
  !!request.requestedByUserId && request.requestedByUserId !== request.playerUserId;

/** A refused link call, in plain words. */
export const familyLinkErrorMessage = (error: unknown): string => {
  if (!(error instanceof TravelBallSignupError)) return 'Something went wrong. Please try again.';
  switch (error.code) {
    case 'invalid_email':
      return 'Enter a valid email address.';
    case 'relationship_required':
      return 'Choose how you are related to the player.';
    case 'invalid_relationship':
      return 'Choose Mother, Father, Guardian or Other.';
    case 'email_not_verified':
      return 'Verify your own email address first. Check your inbox for the link we sent.';
    case 'rate_limited':
      return 'Too many link requests in a row. Wait a minute, then try again.';
    case 'already_decided':
      return 'This request was already answered or cancelled.';
    case 'link_request_expired':
      return 'This request has expired. Ask them to send a new one.';
    case 'link_not_found':
      return 'We could not find that link. It may have been removed.';
    case 'player_cannot_unlink':
      return 'Only a parent or guardian can remove a link. Ask them, or your coach.';
    case 'decline_instead':
      return 'This request was sent to you. Use Decline instead.';
    case 'cannot_link_self':
      return "You can't link to your own account.";
    case 'already_requested':
      return 'You already sent a request to this email.';
    case 'guardian_only':
      return 'Only your parent or guardian can change this.';
    default:
      return familyAccountErrorMessage(error);
  }
};

/** The family account itself was not found (404 `not_found`): never read as travel ball being off. */
export const ACCOUNT_NOT_FOUND_MESSAGE = 'We could not find your account. Sign out, then sign in again.';

/**
 * A refusal any family call can get, whatever it was doing: the account is gone, or the call reached
 * a coach-only route. Anything else keeps the server's own message, which is always displayable.
 */
export const familyAccountErrorMessage = (error: TravelBallSignupError): string => {
  switch (error.code) {
    case 'not_found':
      return ACCOUNT_NOT_FOUND_MESSAGE;
    case 'travel_ball_family_view':
      return 'That page is only for coaches.';
    default:
      return error.message;
  }
};

/** Refusals that mean our copy of the links is out of date, so the list is read again. */
export const isStaleLinkError = (error: unknown): boolean =>
  error instanceof TravelBallSignupError &&
  (error.code === 'already_decided' ||
    error.code === 'link_request_expired' ||
    error.code === 'link_not_found' ||
    error.code === 'decline_instead');

/**
 * Who a sent link request is for: the typed email, or, for a link made by accepting a coach's roster
 * invite (no typed email), the player's first name.
 */
export const outgoingLinkTarget = (link: TravelBallFamilyOutgoingLink): string =>
  link.email || link.playerFirstName || 'your player';

/** A sent link request's main line: the email, or "Waiting for Riley to accept (Red Sox 14U)". */
export const outgoingLinkTitle = (link: TravelBallFamilyOutgoingLink): string => {
  if (link.email) return link.email;
  const player = link.playerFirstName || 'your player';
  return link.teamName ? `Waiting for ${player} to accept (${link.teamName})` : `Waiting for ${player} to accept`;
};

/** A guardian's linked player still waiting to join: "Waiting for Riley to join Red Sox 14U." */
export const awaitingPlayerSentence = (player: Pick<TravelBallFamilyPlayer, 'firstName' | 'awaitingTeamName'>): string => {
  const name = player.firstName.trim() || 'your player';
  return player.awaitingTeamName ? `Waiting for ${name} to join ${player.awaitingTeamName}.` : `Waiting for ${name} to join.`;
};
